<script setup lang="ts">
import { Icon } from '@iconify/vue'
import '@onenetwork/components/video_picker/video_picker.scss'
import { computed, ref, watch } from 'vue'
import defaultVideo from '@/assets/default-video.svg'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { UPLOAD_API } from '@/onenetwork/constants/onenetwork.api.constant.ts'
// import imageCompression from 'browser-image-compression';
import TimesIcon from '@primevue/icons/times';

const props = defineProps({
  width: {
    type: String,
    default: '100%',
  },
  height: {
    type: String,
    default: '100%',
  },
  uuid: {
    type: String,
    default: 'image-picker',
  },
  src: String,
  previewSrc: String,
  upload: {
    type: Boolean,
    default: false,
  },
  folder: {
    type: String,
    default: 'common',
  },
  type: {
    type: String,
    default: 'photo',
  },
  readonly: {
    type: Boolean,
    default: false,
  }
})

const emit = defineEmits(['action:open-fileman', 'action:remove-image', 'action:choose-image', 'action:upload-success'])

const imgsrc = ref(defaultVideo)
const imgpreviewSrc = ref('')
const loading = ref(false)
const path = ref('')
const show = ref(false)

const readonly = computed(() => {
  return props.readonly
})

const handleOpenFileman = () => {
  emit('action:open-fileman', props.uuid)
}

const handleRemoveImage = () => {
  const targetPreview = document.getElementById(`ipk-preview-${props.uuid}`)
  if (targetPreview) {
    targetPreview.src = defaultVideo
  }
  const targetCaption = document.getElementById(`ipk-caption-${props.uuid}`)
  if (targetCaption) {
    targetCaption.textContent = 'Chọn file'
  }
  emit('action:remove-image', props.uuid)
}

const fileInput = ref<HTMLInputElement>(null)
const handleChooseFile = () => {
  if(fileInput.value) {
    const _fileInput = fileInput.value.click()
  }
}

const handleChangeFile = (e: Event) => {

  const file = e?.target?.files[0]
  if(file) {
    emit('action:choose-image', {uuid: props.uuid, file})
    if(props.upload) handleUploadProgress(file)
    e.target.value = ''
  }
}

const handleUploadProgress = async (file: File) => {
  try{
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    }
    // const compressedFile = await imageCompression(file, options);
    // console.log('compressedFile instanceof Blob', compressedFile instanceof Blob); // true
    // console.log(`compressedFile size ${compressedFile.size / 1024 / 1024} MB`); // smaller than maxSizeMB

    const formData = new FormData()
    formData.append('file', file)
    formData.append('uuid', props.uuid)
    formData.append('folder', props.folder)
    formData.append('type', props.type)
    const res = await APIClient.post(UPLOAD_API, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    })

    imgsrc.value = res.data.data?.url?.thumbnail || defaultVideo
    imgpreviewSrc.value = res.data.data?.url?.original || defaultVideo
    path.value = res.data.data?.path

    emit('action:upload-success', {uuid: props.uuid, path: path.value, response: res.data})
  } catch (e) {
    console.error(e)
  }
}

watch(()=>props.src, ()=>{
  if(props.src){
    imgsrc.value = props.src
  }
}, {immediate: true})

watch(()=>props.previewSrc, ()=>{
  if(props.previewSrc){
    imgpreviewSrc.value = props.previewSrc
  }
}, {immediate: true})

</script>

<template>
  <div class="video-picker" :style="{ width: props.width, height: props.height }">
    <template  v-if="!readonly">
      <div class="video-preview" @click="handleChooseFile">
        <Image :id="`ipk-preview-${props.uuid}`" :src="imgsrc"  alt="Image" />
        <div class="preview-icon">
          <Icon icon="material-symbols:video-search" />
        </div>
      </div>
    </template>
    <template v-else>
      <Image alt="Image" :id="`ipk-preview-${props.uuid}`">
        <template #previewicon>
          <i class="pi pi-search"></i>
        </template>
        <template #image>
          <img
            :src="imgsrc"
            :alt="'Video'"
            :width="props.width"
            :height="props.height"
            :class="'cs_image'"
            :style="''"
            @click="() => {
              if (!imgpreviewSrc) return;
              show = true
            }"
          />
        </template>
      </Image>
    </template>
    <div class="video-caption" v-if="!readonly">
      <div>
        <span :id="`ipk-caption-${props.uuid}`">Chọn file</span>
        <span class="remove" @click="handleRemoveImage"
          ><Icon icon="iconamoon:trash-fill" /> Gỡ</span
        >
      </div>
    </div>
    <input style="display: none" ref="fileInput" type="file" @change="handleChangeFile" accept="video/*" />
  </div>
  <div
    v-if="show"
    class="fixed top-0 left-0 h-screen w-screen z-100 p-image-mask p-overlay-mask"
  >
    <div class="absolute top-8 right-8 p-image-toolbar">
      <button class="p-image-action p-image-close-button cursor-pointer" @click="show = false">
        <TimesIcon />
      </button>
    </div>
    <div class="absolute top-0 left-0 h-full w-full flex items-center justify-center">
      <video controls :src="imgpreviewSrc || imgsrc" class="max-w-screen max-h-screen"></video>
    </div>
  </div>
</template>

<style scoped></style>
